import {
    KeyboardEvent,
    KeyboardEventHandler,
    MouseEvent,
    MouseEventHandler,
    ReactElement,
    useEffect,
    useLayoutEffect,
    useRef,
    useState
} from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { token } from "@atlaskit/tokens";

export type Tab = {
    id: string;
    title: string;
    content: ReactElement;
};

type SubnavWithSidebarTabsProps = {
    tabs: Tab[];
    links?: ReactElement;
    defaultTab?: number;
};

const useSafeLayoutEffect =
    typeof window !== "undefined" ? useLayoutEffect : useEffect;

const tabContainerStyles = css({
    display: "grid",
    gridTemplateAreas: `
        "tablist tabpanel"
        "links tabpanel" 
    `,
    gridTemplateColumns: "max-content",
    height: "468px",
    backgroundColor: "#f8f8f8",
    fontFamily: "Charlie Display"
});

const tablistStyles = css({
    display: "flex",
    flexDirection: "column",
    color: "#101214",
    paddingInlineStart: token("space.500")
});

function isValidTab(tabs: Tab[], index: number): boolean {
    return Array.isArray(tabs) && index > -1 && index < tabs.length;
}

type UseTabPanelOptions = {
    deleteFocus: (panel: number) => void;
};

function useTabPanel(
    tab: Tab | undefined,
    index: number,
    { deleteFocus }: UseTabPanelOptions
) {
    const { createOnKeyDown } = useEventHandler({
        deleteFocus: (panel: number) => deleteFocus(panel + 1)
    });

    return (
        tab && (
            // following https://www.w3.org/WAI/ARIA/apg/patterns/tabs/ to allow Delete to break
            // from group of focusable elements, despite this div being noninteractive
            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
            <div
                id={`panel-${tab.id}`}
                role="tabpanel"
                aria-labelledby={`tab-${tab.id}`}
                onKeyDown={createOnKeyDown(index)}
            >
                {tab.content}
            </div>
        )
    );
}

interface UseEventHandlerParams {
    next?: (start: number) => number | boolean;
    previous?: (start: number) => number | boolean;
    select?: (item: number) => void | boolean;
    deleteFocus?: (panel: number) => void | boolean;
}

type EventsHandlers = {
    createOnClick: (index: number) => MouseEventHandler<unknown>;
    createOnKeyDown: (index: number) => KeyboardEventHandler<unknown>;
};

function noop(): boolean {
    return false;
}

function useEventHandler({
    previous = noop,
    next = noop,
    select = noop,
    deleteFocus = noop
}: UseEventHandlerParams): EventsHandlers {
    const keyMap = {
        ArrowUp: (start: number) => previous(start),
        ArrowDown: (start: number) => next(start),
        Delete: (panel: number) => deleteFocus(panel)
    };

    return {
        createOnClick: (index: number) => (_evt: MouseEvent) => select(index),
        createOnKeyDown: (index: number) => {
            return <E extends HTMLElement>(evt: KeyboardEvent<E>) => {
                const moveAction = keyMap[evt.key as keyof typeof keyMap];

                if (!moveAction) {
                    return;
                }

                const next = moveAction(index);
                if (next === false) {
                    return;
                }

                evt.preventDefault();
                if (typeof next !== "undefined" && typeof next !== "boolean") {
                    select(next);
                }
            };
        }
    };
}

type TabProps = {
    isActive: boolean;
    isFocused: boolean;
    tab: Tab;
    onClick: MouseEventHandler<HTMLDivElement>;
    onKeyDown: KeyboardEventHandler<HTMLDivElement>;
};

const Tab = ({ tab, onClick, onKeyDown, isActive, isFocused }: TabProps) => {
    const { id, title } = tab;
    const tabRef = useRef<HTMLDivElement>(null);

    useSafeLayoutEffect(() => {
        if (isFocused && tabRef.current) {
            tabRef.current.focus();
        }
    }, [isFocused]);

    return (
        <div
            id={`tab-${id}`}
            ref={tabRef}
            role="tab"
            onClick={onClick}
            onKeyDown={onKeyDown}
            aria-selected={isActive}
            aria-controls={`panel-${id}`}
            tabIndex={isActive ? 0 : -1}
            css={css({
                "display": "flex",
                "paddingInlineStart": token("space.200"),
                "paddingInlineEnd": token("space.400"),
                "paddingBlockStart": token("space.150"),
                "paddingBlockEnd": token("space.150"),
                "backgroundColor": isActive ? "#fff" : "inherit",
                "fontWeight": 700,
                "color": isActive ? "#1868DB" : "#101214",
                '[aria-selected="true"]': {
                    backgroundColor: "#fff"
                },
                "cursor": "pointer",
                "borderRadius": "0.5rem 0 0 0.5rem"
            })}
        >
            <span>{title}</span>
        </div>
    );
};

// TODO we should refactor out the Tab primitive into WPL or use an
// existing ARIA-friendly primitive library
export const SubnavWithSidebarTabs = ({
    tabs,
    links,
    defaultTab = 0
}: SubnavWithSidebarTabsProps) => {
    const intl = useIntl();

    const [activeTabIndex, setActiveTabIndex] = useState(() => {
        if (isValidTab(tabs, defaultTab)) {
            return defaultTab;
        }

        return 0;
    });
    const [focusTab, setFocusTab] = useState<number>();

    const selectFocusableTab = (tab: number): void => {
        let nextTab = 0;

        if (tab < tabs.length) {
            nextTab = tab;
        } else if (tab - 2 >= 0) {
            nextTab = tab - 2;
        }

        setActiveTabIndex(nextTab);
        setFocusTab(nextTab);
    };

    const activeTabPanel = useTabPanel(tabs[activeTabIndex], activeTabIndex, {
        deleteFocus: selectFocusableTab
    });

    const { createOnClick, createOnKeyDown } = useEventHandler({
        previous: (start: number) => {
            const nextIndex = start - 1;

            return isValidTab(tabs, nextIndex) ? nextIndex : tabs.length - 1;
        },
        next: (start: number) => {
            const nextIndex = start + 1;

            return isValidTab(tabs, nextIndex) ? nextIndex : 0;
        },
        select: selectFocusableTab
    });

    return (
        <div css={tabContainerStyles}>
            <div
                css={css({
                    gridArea: "tablist",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    backgroundColor: "#f8f8f8",
                    paddingBlockStart: "40px",
                    height: "max-content"
                })}
            >
                <div
                    role="tablist"
                    aria-label={intl.formatMessage({
                        id: "globalnav.v2.subnav.tabs.aria-label-tablist",
                        defaultMessage: "Products by user category"
                    })}
                    aria-orientation="vertical"
                    css={tablistStyles}
                >
                    {tabs.map((tab, index) => {
                        const isActive = index === activeTabIndex;
                        const isFocused = index === focusTab;

                        return (
                            <Tab
                                key={`key-${tab.id}`}
                                isActive={isActive}
                                isFocused={isFocused}
                                tab={tab}
                                onClick={createOnClick(index)}
                                onKeyDown={createOnKeyDown(activeTabIndex)}
                            />
                        );
                    })}
                </div>
            </div>
            <div
                css={css({
                    gridArea: "tabpanel",
                    backgroundColor: "#fff"
                })}
            >
                {activeTabPanel}
            </div>
            {links ?? null}
        </div>
    );
};
